import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu, Bell, Search, LogOut, User, Moon, Sun, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsApi, type VendorNotification } from '../../api/notifications';
import { useNotificationsUnread } from '../../hooks/useNotificationsUnread';
import { usePortalNavigation } from '../../hooks/usePortalNavigation';
import { portalPageEnabled } from '../../utils/portalNavigation';
import { authPath, scopedPath } from '../../utils/shopSlug';

interface HeaderProps {
  onMenuClick: () => void;
}

const DARK_MODE_KEY = 'vendoriq-dark-mode';

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigation = usePortalNavigation();
  const profileEnabled = portalPageEnabled('/profile', navigation);
  const notificationsEnabled = portalPageEnabled('/notifications', navigation);
  const ordersEnabled = portalPageEnabled('/orders', navigation);
  const productsEnabled = portalPageEnabled('/products', navigation);
  const searchEnabled = ordersEnabled || productsEnabled;
  const searchLabel = ordersEnabled && productsEnabled ? 'Search orders and products'
    : ordersEnabled ? 'Search orders' : 'Search products';
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem(DARK_MODE_KEY);
    if (stored !== null) {
      return stored === 'true';
    }
    return document.documentElement.classList.contains('dark');
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  // The bell was a hardcoded stub: an ALWAYS-visible red dot and a static
  // "No new notifications" panel that never called the API. A vendor with 12
  // unread notifications saw a permanent alert badge and an empty dropdown,
  // which trains people to ignore the bell entirely. It is now real.
  //
  // The unread count is a react-query CONSUMER of ['notifications','unread']
  // (not the old useState+setInterval). This is the seam that makes realtime
  // actually reach the badge: useVendorWebSocket invalidates that key on a
  // push, so the count re-fetches the instant a notification arrives; the
  // 60s refetchInterval inside the hook stays as the offline/no-socket
  // fallback. The recent-list dropdown is likewise a query keyed under
  // ['notifications', ...] so the same WS invalidation refreshes it.
  const unreadCount = useNotificationsUnread(notificationsEnabled);

  const { data: recentData, isLoading: notificationsLoading } = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => notificationsApi.list({ per_page: 5 }),
    enabled: showNotifications && notificationsEnabled,
    staleTime: 5_000,
  });
  const recentNotifications: VendorNotification[] = recentData?.items ?? [];

  // Keep the document theme synchronized with the persisted UI preference.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    if (!notificationsEnabled) setShowNotifications(false);
    if (!searchEnabled) setMobileSearchOpen(false);
  }, [notificationsEnabled, searchEnabled]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setShowDropdown(false);
      setShowNotifications(false);
      setMobileSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem(DARK_MODE_KEY, String(next));
  };

  const handleLogout = async () => {
    // Capture the marketplace slug BEFORE logout() clears localStorage —
    // we want the vendor's "Sign back in" page to remember which
    // marketplace they were just signed out of, even though their
    // session is gone. authPath() prefers the URL slug, which is still
    // present at logout time (we're inside the authenticated routes).
    const loginUrl = authPath('login');
    try {
      await logout();
    } catch {
      // AuthContext already removes local identity and private caches, even
      // if server logout fails. Finish the visible sign-out navigation.
    } finally {
      navigate(loginUrl);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const destination = ordersEnabled && (!productsEnabled || q.includes('order') || q.startsWith('#'))
        ? '/orders' : productsEnabled ? '/products' : null;
      if (!destination) return;
      navigate(scopedPath(`${destination}?search=${encodeURIComponent(searchQuery.trim())}`));
      setSearchQuery('');
      setMobileSearchOpen(false);
    }
  };

  return (
    <header className="relative z-30 flex h-16 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-800 lg:px-6">
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 lg:hidden"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
        {searchEnabled && <div className="relative hidden sm:block">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={`${searchLabel}…`}
            aria-label={searchLabel}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="w-64 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          />
        </div>}
      </div>

      <div className="flex items-center gap-3">
        {searchEnabled && <button
          type="button"
          onClick={() => setMobileSearchOpen((current) => !current)}
          aria-label={mobileSearchOpen ? 'Close search' : searchLabel}
          aria-expanded={mobileSearchOpen}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary dark:text-gray-400 dark:hover:bg-gray-700 sm:hidden"
        >
          {mobileSearchOpen ? (
            <X className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Search className="h-5 w-5" aria-hidden="true" />
          )}
        </button>}

        <button
          type="button"
          onClick={toggleDarkMode}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary dark:text-gray-400 dark:hover:bg-gray-700"
        >
          {darkMode ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
        </button>

        {notificationsEnabled && <div className="relative" ref={notificationRef}>
          <button
            type="button"
            onClick={() => setShowNotifications(!showNotifications)}
            aria-label={
              unreadCount > 0
                ? `Notifications, ${unreadCount} unread`
                : 'Notifications'
            }
            aria-expanded={showNotifications}
            className="relative rounded-lg p-2 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary dark:text-gray-400 dark:hover:bg-gray-700"
          >
            <Bell className="h-5 w-5" aria-hidden="true" />
            {unreadCount > 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white"
                aria-hidden="true"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div
              role="dialog"
              aria-label="Recent notifications"
              className="absolute right-0 top-full mt-1 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Notifications
                </h3>
                <button
                  onClick={() => setShowNotifications(false)}
                  aria-label="Close notifications"
                  className="rounded text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              {notificationsLoading ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
                </div>
              ) : recentNotifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell className="mx-auto mb-2 h-8 w-8 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No new notifications
                  </p>
                </div>
              ) : (
                <ul className="max-h-80 divide-y divide-gray-100 overflow-y-auto dark:divide-gray-700">
                  {recentNotifications.map((n) => (
                    <li key={n.id}>
                      <button
                        onClick={() => {
                          setShowNotifications(false);
                          navigate(scopedPath('/notifications'));
                        }}
                        className="block w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <p
                          className={`text-sm ${
                            n.is_read
                              ? 'text-gray-600 dark:text-gray-300'
                              : 'font-semibold text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {n.title}
                        </p>
                        <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                          {n.message}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="border-t border-gray-200 px-4 py-2 dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowNotifications(false);
                    navigate(scopedPath('/notifications'));
                  }}
                  className="w-full text-center text-xs font-medium text-primary hover:underline"
                >
                  View all notifications
                </button>
              </div>
            </div>
          )}
        </div>}

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            aria-label="User menu"
            aria-expanded={showDropdown}
            className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary dark:hover:bg-gray-700"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
              {user?.business_name?.charAt(0).toUpperCase() || 'V'}
            </div>
            <span className="hidden text-sm font-medium text-gray-700 dark:text-gray-300 md:block">
              {user?.business_name || 'Vendor'}
            </span>
          </button>

          {showDropdown && (
            <div className="absolute right-0 top-full mt-1 w-48 rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
              {profileEnabled && <button
                onClick={() => {
                  setShowDropdown(false);
                  navigate(scopedPath('/profile'));
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <User className="h-4 w-4" />
                Profile
              </button>}
              {profileEnabled && <hr className="my-1 border-gray-200 dark:border-gray-700" />}
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:text-red-400 dark:hover:bg-gray-700"
              >
                <LogOut className="h-4 w-4" />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>

      {mobileSearchOpen && searchEnabled && (
        <div className="absolute left-0 right-0 top-full border-b border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800 sm:hidden">
          <label htmlFor="mobile-global-search" className="sr-only">
            {searchLabel}
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              id="mobile-global-search"
              autoFocus
              type="search"
              placeholder={`${searchLabel}…`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
        </div>
      )}
    </header>
  );
}
